import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const instantSchema = z.iso.datetime({ offset: true })
const noteSchema = z.string().trim().min(1).max(2_000)

export const visitCompletionOutcomeV1Schema = z.enum([
  "Completed",
  "Partial",
  "Return Required",
  "No Access",
  "Weather",
  "Equipment/Material Issue",
  "Customer Canceled/Refused",
])

export const customerAcknowledgmentV1Schema = z.object({
  status: z.enum(["Signed", "Declined", "Unavailable", "Not Requested"]),
  customerName: z.string().trim().min(1).max(160).nullable(),
  signatureMediaId: entityIdSchema.nullable(),
  reason: noteSchema.nullable(),
}).strict().superRefine((value, context) => {
  const signed = value.status === "Signed"
  if (
    signed !==
    (value.customerName !== null && value.signatureMediaId !== null)
  ) {
    context.addIssue({
      code: "custom",
      message: "A signed acknowledgment requires customer and signature evidence.",
      path: ["status"],
    })
  }
  if (!signed && value.status !== "Not Requested" && value.reason === null) {
    context.addIssue({
      code: "custom",
      message: "A declined or unavailable acknowledgment requires a reason.",
      path: ["reason"],
    })
  }
})

export const visitCompletionSubmitV1Schema = z.object({
  completionId: entityIdSchema,
  exceptionId: entityIdSchema.nullable(),
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  visitTimeId: entityIdSchema,
  visitTimeVersion: z.number().int().positive(),
  timerEndEventId: entityIdSchema,
  outcome: visitCompletionOutcomeV1Schema,
  performedScope: noteSchema,
  remainingScope: noteSchema.nullable(),
  reason: noteSchema.nullable(),
  attemptedContact: noteSchema.nullable(),
  evidenceMediaIds: z.array(entityIdSchema).min(1).max(25),
  customerAcknowledgment: customerAcknowledgmentV1Schema,
  capturedAt: instantSchema,
}).strict().superRefine((value, context) => {
  const completed = value.outcome === "Completed"
  if (
    completed !== (value.exceptionId === null) ||
    completed !== (value.remainingScope === null) ||
    completed !== (value.reason === null)
  ) {
    context.addIssue({
      code: "custom",
      message: "Only incomplete outcomes carry exception details.",
      path: ["outcome"],
    })
  }
  if (completed && value.customerAcknowledgment.status !== "Signed") {
    context.addIssue({
      code: "custom",
      message: "Completed work requires signed customer acknowledgment.",
      path: ["customerAcknowledgment", "status"],
    })
  }
  if (
    value.outcome === "No Access" &&
    value.attemptedContact === null
  ) {
    context.addIssue({
      code: "custom",
      message: "No Access requires attempted-contact details.",
      path: ["attemptedContact"],
    })
  }
  if (
    value.customerAcknowledgment.signatureMediaId !== null &&
    !value.evidenceMediaIds.includes(
      value.customerAcknowledgment.signatureMediaId,
    )
  ) {
    context.addIssue({
      code: "custom",
      message: "Signature evidence must be completion evidence.",
      path: ["customerAcknowledgment", "signatureMediaId"],
    })
  }
})

export const completionDispositionV1Schema = z.enum([
  "Accept Completed Portion",
  "Non-Invoiceable",
  "Cancellation Fee Review",
  "Create Return Work",
  "Change Order Required",
  "Hold Billing",
  "Contact Customer",
  "Close No Further Work",
])

export const completionExceptionDispositionV1Schema = z.object({
  dispositionId: entityIdSchema,
  exceptionId: entityIdSchema,
  completionId: entityIdSchema,
  dispositions: z.array(completionDispositionV1Schema).min(1).max(4),
  decisionReason: noteSchema,
  followUpServiceOrderId: entityIdSchema.nullable(),
  followUpVisitId: entityIdSchema.nullable(),
  followUpStartsAt: instantSchema.nullable(),
  followUpEndsAt: instantSchema.nullable(),
  followUpTimezone: z.string().trim().min(1).max(64).nullable(),
}).strict().superRefine((value, context) => {
  const createsReturn = value.dispositions.includes("Create Return Work")
  const followUpValues = [
    value.followUpServiceOrderId,
    value.followUpVisitId,
    value.followUpStartsAt,
    value.followUpEndsAt,
    value.followUpTimezone,
  ]
  if (
    createsReturn !== followUpValues.every((candidate) => candidate !== null)
  ) {
    context.addIssue({
      code: "custom",
      message: "Return work requires one complete follow-up schedule.",
      path: ["dispositions"],
    })
  }
  if (
    value.followUpStartsAt !== null &&
    value.followUpEndsAt !== null &&
    Date.parse(value.followUpEndsAt) <= Date.parse(value.followUpStartsAt)
  ) {
    context.addIssue({
      code: "custom",
      message: "Follow-up end must be after its start.",
      path: ["followUpEndsAt"],
    })
  }
})

export type VisitCompletionSubmitV1 = z.infer<
  typeof visitCompletionSubmitV1Schema
>
export type CompletionExceptionDispositionV1 = z.infer<
  typeof completionExceptionDispositionV1Schema
>
